// src/lib/tanstack-i18n-backend.ts
import type { BackendModule, Services, InitOptions, ReadCallback, MultiReadCallback } from "i18next";

class TanStackBackend implements BackendModule {
  static type = "backend" as const;
  type = "backend" as const;
  
  private services?: Services;
  private backendOptions?: any;
  private i18nextOptions?: InitOptions;

  init(services: Services, backendOptions: any = {}, i18nextOptions: InitOptions = {}) {
    this.services = services;
    this.backendOptions = backendOptions;
    this.i18nextOptions = i18nextOptions;
  }

  read(
    language: string,
    namespace: string,
    callback: ReadCallback
  ) {
    // Use direct dynamic import instead of server function
    // This works in both server and client contexts
    import(`../../locales/${language}/${namespace}.json`)
      .then((translationModule) => {
        const translations = translationModule.default || translationModule;
        callback(null, translations);
      })
      .catch((error) => {
        console.error(`Failed to load translations: ${language}/${namespace}`, error);
        callback(error, null);
      });
  }

  // Optional: Implement readMulti for better performance with multiple languages/namespaces
  readMulti(
    languages: readonly string[],
    namespaces: readonly string[],
    callback: MultiReadCallback
  ) {
    const result: Record<string, Record<string, any>> = {};
    const promises: Promise<void>[] = [];

    for (const lng of languages) {
      result[lng] = {};
      for (const ns of namespaces) {
        promises.push(
          import(`../../locales/${lng}/${ns}.json`)
            .then((translationModule) => {
              result[lng][ns] = translationModule.default || translationModule;
            })
            .catch((error) => {
              console.error(`Failed to load translations: ${lng}/${ns}`, error);
              result[lng][ns] = {};
            })
        );
      }
    }

    Promise.all(promises)
      .then(() => {
        callback(null, result);
      })
      .catch((error) => {
        console.error("Failed to load multiple translations:", error);
        callback(error, null);
      });
  }

  // Optional: Implement create for saving missing translations
  create(
    languages: readonly string[],
    namespace: string,
    key: string,
    fallbackValue: string
  ) {
    // This would be called when a translation key is missing
    // You could implement logging or saving missing keys here
    console.warn(
      `Missing translation: ${languages.join(", ")}:${namespace}.${key} (fallback: ${fallbackValue})`
    );
  }
}

export default TanStackBackend;
